// ============================================================================
// Map filters + legend (WS9; WS10 adds search/region/toggles).
//
// THE SIGNATURE BEHAVIOUR: pin colour is driven by the active dimension, and
// the legend restates the colour contract on every change. Legend keys are
// themselves toggles — tap to mute a bucket (or, in subcontractor mode, tap
// to SELECT-AND-HIGHLIGHT: 70 active subs never get one colour each; selected
// subs get strong distinct colours, everything else mutes to grey — Ben's
// use case is "show me EM's facilities and Rodrigo's so I can decide who
// gets the new one").
//
// account_status_rag is the only RAG dimension. The second one
// (sub_status_rag) was retired 2026-07-31 — see
// tcs-app-prompt-retire-sub-rag-2026-07-31.md. Do not add another.
// ============================================================================

import { useMemo, useState } from 'react';
import type { MapDimension, MapFacility } from './map-types';
import {
  bucketOf,
  buildPalette,
  mutedColor,
  SUB_HIGHLIGHT_POOL,
  TYPE_LABELS,
  unsetColor,
} from './map-colors';

// 'sentiment' was de-scoped as a filter dimension on 2026-07-31 (Ben: TCS
// doesn't filter by it) and its machinery was removed entirely on 2026-08-03
// along with the stored facilities.account_sentiment column it read. Sentiment
// is DERIVED from facility_feedback and is not a map dimension — see
// tcs-app-prompt-retire-account-sentiment-2026-08-03.md. 'subrag' went the
// same way on 2026-07-31 when sub_status_rag was retired.
const DIMENSIONS: { key: MapDimension; label: string }[] = [
  { key: 'type', label: 'Facility type' },
  { key: 'am', label: 'Account manager' },
  { key: 'sub', label: 'Subcontractor' },
  { key: 'acctrag', label: 'Account RAG' },
  { key: 'network', label: 'Network' },
];

const LEGEND_TITLES: Record<MapDimension, string> = {
  type: 'Coloured by facility type',
  am: 'Coloured by account manager',
  sub: 'Subcontractor — tap to isolate',
  acctrag: 'Coloured by account status',
  network: 'Coloured by network / account',
};

const LEGEND_HINTS: Partial<Record<MapDimension, string>> = {
  sub: 'selected subs highlight; the rest grey out',
  acctrag: 'status colours — reserved',
  network: 'top networks by facility count',
};

export interface MapFiltersState {
  visible: MapFacility[];
  dimension: MapDimension;
  setDimension: (d: MapDimension) => void;
  palette: Map<string, string>;
  mutedBuckets: Set<string>;
  toggleBucket: (b: string) => void;
  selectedSubs: Set<string>;
  // WS10 — search, region, toggles. No radius filter (Ben's decision: the
  // map opens centred on the user and pans/zooms freely; no PostGIS needed).
  search: string;
  setSearch: (s: string) => void;
  region: string;
  setRegion: (r: string) => void;
  regions: string[];
  showClosed: boolean;
  setShowClosed: (v: boolean) => void;
  showOffices: boolean;
  setShowOffices: (v: boolean) => void;
  hasOpenTasks: boolean;
  setHasOpenTasks: (v: boolean) => void;
  /** ordered legend entries for the active dimension */
  legend: { bucket: string; label: string; color: string; count: number }[];
  shownCount: number;
  totalCount: number;
}

export function useMapFilters(
  scoped: MapFacility[],
  initialDimension: MapDimension = 'type',
): MapFiltersState {
  const [dimension, setDimensionRaw] = useState<MapDimension>(initialDimension);
  const [mutedBuckets, setMutedBuckets] = useState<Set<string>>(new Set());
  const [selectedSubs, setSelectedSubs] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [region, setRegion] = useState('all');
  // Closed: default OFF, toggle reveals grey pins. Offices: default ON.
  const [showClosed, setShowClosed] = useState(false);
  const [showOffices, setShowOffices] = useState(true);
  const [hasOpenTasks, setHasOpenTasks] = useState(false);

  const regions = useMemo(
    () =>
      [...new Set(scoped.map((f) => f.market_region).filter(Boolean))].sort() as string[],
    [scoped],
  );

  const rows = useMemo(() => {
    let r = showClosed ? scoped : scoped.filter((f) => f.status !== 'Closed');
    if (region !== 'all') r = r.filter((f) => f.market_region === region);
    if (hasOpenTasks) r = r.filter((f) => f.open_task_count > 0);
    const q = search.trim().toLowerCase();
    if (q) {
      r = r.filter((f) => {
        const hay = [
          f.facility_name,
          f.facility_address,
          f.provider_name,
          ...(f.provider_names ?? []),
          f.account_manager_name,
          f.account_name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    }
    return r;
  }, [scoped, showClosed, region, hasOpenTasks, search]);

  const setDimension = (d: MapDimension) => {
    setDimensionRaw(d);
    setMutedBuckets(new Set());
    setSelectedSubs(new Set());
  };

  const basePalette = useMemo(() => buildPalette(rows, dimension), [rows, dimension]);

  // sub mode: palette is selection-driven, in selection order
  const palette = useMemo(() => {
    if (dimension !== 'sub') return basePalette;
    const p = new Map<string, string>();
    let i = 0;
    for (const s of selectedSubs) {
      p.set(s, SUB_HIGHLIGHT_POOL[i % SUB_HIGHLIGHT_POOL.length]);
      i += 1;
    }
    return p;
  }, [basePalette, dimension, selectedSubs]);

  const toggleBucket = (b: string) => {
    if (dimension === 'sub') {
      setSelectedSubs((prev) => {
        const next = new Set(prev);
        if (next.has(b)) next.delete(b);
        else next.add(b);
        return next;
      });
      return;
    }
    setMutedBuckets((prev) => {
      const next = new Set(prev);
      if (next.has(b)) next.delete(b);
      else next.add(b);
      return next;
    });
  };

  const legend = useMemo(() => {
    const tally = new Map<string, number>();
    for (const f of rows) {
      const b = bucketOf(f, dimension);
      tally.set(b, (tally.get(b) ?? 0) + 1);
    }
    // Size-ordered; in sub mode the SELECTED subs float to the front so the
    // chips you're comparing are always visible without scrolling (Ben,
    // 2026-07-31: 70 wrapped chips pushed the map off-screen on phone).
    const entries = [...tally.entries()].sort((a, b) => {
      if (dimension === 'sub') {
        const selA = selectedSubs.has(a[0]) ? 1 : 0;
        const selB = selectedSubs.has(b[0]) ? 1 : 0;
        if (selA !== selB) return selB - selA;
      }
      return b[1] - a[1];
    });
    return entries.map(([bucket, count]) => ({
      bucket,
      label:
        dimension === 'type'
          ? TYPE_LABELS[bucket] ?? (bucket === '—' ? 'Not set' : bucket)
          : bucket === '—'
            ? 'Not set'
            : bucket,
      color:
        dimension === 'sub'
          ? palette.get(bucket) ?? mutedColor()
          : palette.get(bucket) ?? unsetColor(),
      count,
    }));
  }, [dimension, palette, rows, selectedSubs]);

  const shownCount = useMemo(() => {
    if (dimension === 'sub') {
      if (selectedSubs.size === 0) return rows.length;
      return rows.filter((f) => selectedSubs.has(bucketOf(f, 'sub'))).length;
    }
    return rows.filter((f) => !mutedBuckets.has(bucketOf(f, dimension))).length;
  }, [dimension, mutedBuckets, rows, selectedSubs]);

  return {
    visible: rows,
    dimension,
    setDimension,
    palette,
    mutedBuckets,
    toggleBucket,
    selectedSubs,
    search,
    setSearch,
    region,
    setRegion,
    regions,
    showClosed,
    setShowClosed,
    showOffices,
    setShowOffices,
    hasOpenTasks,
    setHasOpenTasks,
    legend,
    shownCount,
    totalCount: rows.length,
  };
}

export function MapFilterBar({
  filters,
  trailing,
  compact = false,
}: {
  filters: MapFiltersState;
  /** optional consumer-owned controls rendered at the end of the toggle row
   *  (e.g. the Route Optimizer's "Plan route" button — the bar itself stays
   *  route-agnostic; this is just a slot). */
  trailing?: React.ReactNode;
  /**
   * CW-I Phase 3b — fullscreen mode. Collapses the bar to ONE horizontal
   * scrolling chip row: search/region and the two-row legend are dropped so
   * the map gets the screen. Nothing is unmounted and no filter state is
   * touched — `compact` only changes what is rendered, so entering and
   * leaving fullscreen preserves every active filter.
   */
  compact?: boolean;
}) {
  const {
    dimension,
    setDimension,
    legend,
    mutedBuckets,
    selectedSubs,
    toggleBucket,
    shownCount,
    totalCount,
    search,
    setSearch,
    region,
    setRegion,
    regions,
    showClosed,
    setShowClosed,
    showOffices,
    setShowOffices,
    hasOpenTasks,
    setHasOpenTasks,
  } = filters;

  return (
    <div className="border-b border-tcs-border bg-tcs-bg px-3 pt-2">
      {/* WS10 — live search (no submit), region, toggles. No radius filter.
          Hidden in compact (fullscreen) mode — the dimension chips and the
          shown/total counter below carry the load there. */}
      <div className={compact ? 'hidden' : 'flex items-center gap-2 pb-2'}>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search facility, city, sub, AM, account"
          aria-label="Search facilities"
          className="min-w-0 flex-1 rounded-lg border border-tcs-border bg-tcs-field px-3 py-1.5 text-[16px] outline-none placeholder:text-tcs-text-muted/60 md:text-sm"
        />
        <select
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          aria-label="Region"
          className="shrink-0 rounded-lg border border-tcs-border bg-tcs-field px-2 py-1.5 text-xs font-medium"
        >
          <option value="all">All regions</option>
          {regions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>
      <div className={(compact ? 'hidden ' : 'flex ') + 'items-center gap-1.5 overflow-x-auto pb-2 [scrollbar-width:none]'}>
        <FilterToggle
          label="Closed facilities"
          pressed={showClosed}
          onToggle={() => setShowClosed(!showClosed)}
        />
        <FilterToggle
          label="TCS offices & sites"
          pressed={showOffices}
          onToggle={() => setShowOffices(!showOffices)}
        />
        <FilterToggle
          label="Has open tasks"
          pressed={hasOpenTasks}
          onToggle={() => setHasOpenTasks(!hasOpenTasks)}
        />
        {!compact && trailing}
      </div>
      {/* THE single row in compact mode: dimension chips + the shown/total
          counter (+ the consumer's trailing control, which is the route
          planner's mode switch and must not disappear in fullscreen). */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 [scrollbar-width:none]">
        {DIMENSIONS.map((d) => (
          <button
            key={d.key}
            type="button"
            aria-pressed={dimension === d.key}
            onClick={() => setDimension(d.key)}
            className={
              'shrink-0 rounded-full border px-3 py-1 text-xs font-medium transition-colors ' +
              (dimension === d.key
                ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink'
                : 'border-tcs-border bg-tcs-raised text-tcs-text-muted hover:text-tcs-text')
            }
          >
            {d.label}
          </button>
        ))}
        <span className="ml-auto shrink-0 font-mono text-[10px] text-tcs-text-muted">
          {shownCount} / {totalCount}
        </span>
        {compact && trailing}
      </div>

      <div className={compact ? 'hidden' : 'flex items-baseline gap-2'}>
        <span className="font-mono text-[10px] uppercase tracking-wider text-tcs-text-muted">
          {LEGEND_TITLES[dimension]}
        </span>
        {LEGEND_HINTS[dimension] && (
          <span className="font-mono text-[10px] text-tcs-text-muted/70">
            {LEGEND_HINTS[dimension]}
          </span>
        )}
      </div>
      {/* Two scrolling rows, fixed height, never wraps further — high-
          cardinality dimensions (70 subs, many networks) must never push the
          map off-screen, but one row made categories too hard to scan
          (Ben, 2026-07-31). grid-flow-col packs chips column-wise so the
          block grows horizontally. */}
      <div
        className={
          (compact ? 'hidden ' : 'grid ') +
          'grid-flow-col grid-rows-2 justify-start justify-items-start gap-1.5 overflow-x-auto py-2 [scrollbar-width:none]'
        }
        role="group"
        aria-label="Legend"
      >
        {legend.map((k) => {
          const active =
            dimension === 'sub'
              ? selectedSubs.has(k.bucket)
              : !mutedBuckets.has(k.bucket);
          // Chip states must be UNMISTAKABLE (Ben, 2026-07-31: 40% opacity
          // alone read as "did the filter even work?").
          //   non-sub hidden  -> dashed border + strike + grey swatch + 'hidden'
          //   sub selected    -> bold ring (these are highlighted on the map)
          //   sub unselected  -> neutral (still visible as grey CONTEXT pins,
          //                      so no strike — that would claim they're gone)
          const isSub = dimension === 'sub';
          const chipClass = isSub
            ? active
              ? 'border-2 border-tcs-text bg-tcs-raised font-semibold text-tcs-text'
              : 'border border-tcs-border bg-tcs-raised text-tcs-text-muted'
            : active
              ? 'border border-tcs-border bg-tcs-raised text-tcs-text'
              : 'border border-dashed border-tcs-text-muted/60 bg-tcs-bg text-tcs-text-muted line-through decoration-2 opacity-70';
          return (
            <button
              key={k.bucket}
              type="button"
              aria-pressed={active}
              onClick={() => toggleBucket(k.bucket)}
              className={
                'flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors ' +
                chipClass
              }
            >
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ background: active ? k.color : mutedColor() }}
                aria-hidden
              />
              {k.label}
              <span className="font-mono text-[9px] no-underline">
                {!isSub && !active ? 'hidden' : k.count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FilterToggle({
  label,
  pressed,
  onToggle,
}: {
  label: string;
  pressed: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onToggle}
      className={
        'flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[11px] font-medium transition-colors ' +
        (pressed
          ? 'border-tcs-border bg-tcs-raised text-tcs-text'
          : 'border-tcs-border bg-tcs-bg text-tcs-text-muted')
      }
    >
      <span
        className={
          'grid h-3 w-3 place-items-center rounded-sm border ' +
          (pressed ? 'border-tcs-inverse bg-tcs-inverse' : 'border-tcs-text-muted')
        }
        aria-hidden
      >
        {pressed && (
          <span className="h-1 w-1.5 -translate-y-px -rotate-45 border-b-[1.5px] border-l-[1.5px] border-tcs-inverse-ink" />
        )}
      </span>
      {label}
    </button>
  );
}
